import React, { useState, useRef, useCallback, memo } from "react";
import Nav from "../components/Nav";
import { useNavContext } from "../context/NavContext";
import Breadcrumb from "../components/Breadcrumb";

export default function CartaNueva() {
  const [isEditing, setIsEditing] = useState(true);

  // Datos base
  const [logoSrc] = useState("/img/logo-comercial.svg");
  const [ciudad, setCiudad] = useState("El Salto, Jal.");
  const [fecha, setFecha] = useState("lunes, 10 de noviembre de 2025");
  const [titulo, setTitulo] = useState("CARTA PERSONALIZADA");
  const [contenido, setContenido] = useState(
    "Por medio de la presente, me permito informar que..."
  );
  const [firma, setFirma] = useState("Nombre del Responsable");
  const [puesto, setPuesto] = useState("Cargo o Puesto");
  const [empresa, setEmpresa] = useState(
    "UNIFICACIÓN PARA LA AUTOMATIZACIÓN SA DE CV"
  );

  const { proximasAVencer, proximosEventos } = useNavContext?.() || {};

  // ===== Editable =====
  const Editable = memo(
    function Editable({
      enabled,
      initialText,
      onCommit,
      underline = false,
      bold = false,
      ariaLabel,
      block = false,
      style,
    }) {
      const ref = useRef(null);
      const [focused, setFocused] = useState(false);
      const inited = useRef(false);

      const setNode = useCallback(
        (node) => {
          if (!node) return;
          ref.current = node;
          if (!inited.current) {
            node.textContent = initialText || "";
            inited.current = true;
          }
        },
        [initialText]
      );

      const handleBlur = useCallback(() => {
        setFocused(false);
        if (onCommit && ref.current) onCommit(ref.current.textContent || "");
      }, [onCommit]);

      const handleFocus = useCallback(() => setFocused(true), []);

      const handleKeyDown = useCallback(
        (e) => {
          if (!block && e.key === "Enter") e.preventDefault();
        },
        [block]
      );

      const handlePaste = useCallback(
        (e) => {
          if (!enabled) return;
          e.preventDefault();
          const text = (e.clipboardData || window.clipboardData).getData(
            "text"
          );
          document.execCommand("insertText", false, text);
        },
        [enabled]
      );

      const redBase = "#ef4444";
      const redLight = "#fca5a5";
      const redBg = "#fef2f2";

      const editableBox = enabled
        ? {
            background: redBg,
            border: `1px dashed ${focused ? redBase : redLight}`,
            borderRadius: 6,
            boxShadow: focused ? "0 0 0 2px rgba(239,68,68,.10)" : "none",
            transition: "box-shadow .15s ease, border-color .15s ease",
          }
        : {};

      return (
        <span
          ref={setNode}
          role="textbox"
          aria-label={ariaLabel}
          contentEditable={enabled}
          suppressContentEditableWarning
          spellCheck={false}
          onBlur={enabled ? handleBlur : undefined}
          onFocus={enabled ? handleFocus : undefined}
          onKeyDown={enabled ? handleKeyDown : undefined}
          onPaste={enabled ? handlePaste : undefined}
          className="editable-field"
          style={{
            boxSizing: "border-box",
            textDecoration: underline ? "underline" : "none",
            fontWeight: bold ? 800 : undefined,
            cursor: enabled ? "text" : "default",
            display: block ? "block" : "inline",
            padding: enabled ? "2px 4px" : 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            ...editableBox,
            ...style,
          }}
        />
      );
    },
    () => true
  );

  // ===== Estilos =====

  const GAP = 20;

  const outer = {
    background: "#f5f7fa",
    minHeight: "100dvh",
    paddingBlock: GAP,
    display: "flex",
    flexDirection: "column",
  };

  const page = {
    width: "92%",
    maxWidth: 1180,
    margin: `${GAP}px auto`, // ↑↓ = 20px, ←→ = auto (centrado)
    background: "#fff",
    padding: "60px 90px 80px",
    fontFamily: "Arial, Helvetica, sans-serif",
    color: "#111",
    lineHeight: 1.6,
    boxShadow: "0 2px 18px rgba(0,0,0,0.08)",
    minHeight: 1300,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    position: "relative",
  };

  const content = {
    maxWidth: 1020,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  };

  const header = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 25,
  };

  const logoStyle = {
    height: 65,
    marginRight: 20,
  };

  const cityDate = {
    textAlign: "right",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.4,
    marginTop: 10,
  };

  const blueRule = {
    height: 6,
    width: "100%",
    background: "#0A6CA9",
    borderRadius: 2,
    marginTop: 25,
    marginBottom: 35,
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };

  const footerBarsWrap = {
    display: "flex",
    width: "100%",
    height: 22,
    marginTop: 48,
  };

  const footerBlue = {
    background: "#0A6CA9",
    width: "60%",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };

  const footerDark = {
    background: "#3D3D3D",
    width: "40%",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };

  const handlePrint = () => {
    const wasEditing = isEditing;
    setIsEditing(false); // oculta bordes de edición

    requestAnimationFrame(() => {
      const pageNode = document.querySelector(".cn-page");
      if (!pageNode) return;

      const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Carta Nueva</title>
<style>
  /* 1) Deja que el navegador pagine: márgenes del documento */
  @page { size: A4; margin: 6mm 12mm 16mm 12mm; } /* bottom >= alto del footer */

  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body {
    margin:0; padding:0; height:100%;
    font-family: Arial, Helvetica, sans-serif;
    color:#111; line-height:1.5; font-size:13.5px;
    background:#fff;
  }

  /* 2) Nada de alturas fijas ni recortes */
  .print-sheet{ position:relative; height:auto; overflow:visible; }

  .cn-page{
    box-shadow:none !important;
    margin:0 !important;
    padding:0 !important;
    background:#fff !important;
    height:auto;                 /* <- clave */
    overflow:visible;            /* <- clave */
    display:flex; flex-direction:column;
  }

  /* 3) Área útil del contenido (menos aire arriba) */
  .cn-content,
  .cn-page > div{
    max-width:none !important;
    margin:4mm 0 0 0 !important;  /* el @page ya pone 6/12/16/12mm */
    padding:0 !important;
    box-sizing:border-box;
  }

  /* 4) Quita totalmente los bordes rojos de edición */
  .editable-field{
    background:transparent !important;
    border:none !important;
    box-shadow:none !important;
    padding:0 !important;
  }

  /* 5) Footer fijo repetido en cada hoja, dentro de los márgenes */

  .footer-blue{ background:#0A6CA9; width:60%; }
  .footer-dark{ background:#3D3D3D; width:40%; }

  /* 6) Permite que el contenido parta entre páginas.
        (Evita "avoid" global que bloquea la paginación) */
  h1, h2, img { break-inside: avoid; page-break-inside: avoid; }
</style>
</head>
<body>
  <div class="print-sheet">${pageNode.outerHTML}</div>
</body>
</html>`.trim();

      // Imprimir en iframe oculto (mismo patrón que usas en otros)
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, {
        position: "fixed",
        right: 0,
        bottom: 0,
        width: "0",
        height: "0",
        border: 0,
      });
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      let finished = false;
      const cleanup = () => {
        if (finished) return;
        finished = true;
        setIsEditing(wasEditing);
        setTimeout(() => iframe.remove(), 0);
      };

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 0);
      };
      iframe.contentWindow.addEventListener("afterprint", cleanup, {
        once: true,
      });
      setTimeout(cleanup, 5000);
    });
  };

  // ===== Render =====
  return (
    <>
      <Nav
        handleSearch={() => {}}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      {/* Barra superior */}
      <div
        className="task-filters"
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap", // todo en una sola fila
          gap: 8,
          borderBottom: "none", // quita la línea
        }}
      >
        {/* Izquierda: Breadcrumb */}
        <div
          style={{
            flex: 1,
            padding: "0 4px",
            minHeight: 36, // mismo alto visual que tus headers
            display: "flex",
            alignItems: "center",
          }}
        >
          <Breadcrumb
            aliases={{
              personalizacion: "Personalizacion",
              "carta-poder": "Carta Poder",
            }}
          />
        </div>

        {/* Derecha: Botones */}
        <div
          className="task-filters-right"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <button
            className="exportar-button"
            onClick={handlePrint}
            title="Imprimir"
          >
            Imprimir
          </button>
          <button
            className="new-task-button"
            onClick={() => setIsEditing((v) => !v)}
            title={isEditing ? "Guardar" : "Editar"}
          >
            {isEditing ? "Guardar" : "Editar"}
          </button>
        </div>
      </div>

      {/* Página */}
      <div style={outer}>
        <div style={page} className="cn-page">
          <div style={content}>
            {/* Encabezado */}
            <div style={header}>
              <img src={logoSrc} alt="Logo" style={logoStyle} />
              <div style={cityDate}>
                <Editable
                  enabled={isEditing}
                  initialText={ciudad}
                  onCommit={setCiudad}
                  ariaLabel="Ciudad"
                  block
                />
                <Editable
                  enabled={isEditing}
                  initialText={fecha}
                  onCommit={setFecha}
                  ariaLabel="Fecha"
                  block
                />
              </div>
            </div>

            {/* Línea azul */}
            <div style={blueRule}></div>

            {/* Título */}
            <h2
              style={{
                textAlign: "center",
                fontSize: 38,
                letterSpacing: 0.3,
                fontWeight: 800,
                marginBottom: 32,
              }}
            >
              <Editable
                enabled={isEditing}
                initialText={titulo}
                onCommit={setTitulo}
                bold
                ariaLabel="Título"
                block
              />
            </h2>

            {/* Contenido principal */}
            <p
              style={{
                fontSize: 22,
                textAlign: "justify",
                marginBottom: 80,
                whiteSpace: "pre-wrap",
                lineHeight: 1.8,
                minHeight: 200,
                transition: "all 0.2s ease-in-out",
              }}
            >
              <Editable
                enabled={isEditing}
                initialText={contenido}
                onCommit={setContenido}
                ariaLabel="Contenido"
                block
              />
            </p>

            {/* Firma */}
            <div style={{ marginTop: 100, textAlign: "center" }}>
              <div
                style={{
                  width: 320,
                  height: 2,
                  background: "#111",
                  margin: "0 auto 14px",
                }}
              ></div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                <Editable
                  enabled={isEditing}
                  initialText={firma}
                  onCommit={setFirma}
                  bold
                  ariaLabel="Firma"
                  block
                />
              </div>
              <div style={{ fontSize: 20 }}>
                <Editable
                  enabled={isEditing}
                  initialText={puesto}
                  onCommit={setPuesto}
                  ariaLabel="Puesto"
                  block
                />
              </div>
              <div style={{ fontSize: 20, marginTop: 4 }}>
                <Editable
                  enabled={isEditing}
                  initialText={empresa}
                  onCommit={setEmpresa}
                  ariaLabel="Empresa"
                  block
                />
              </div>
            </div>

            {/* Barras pie */}
            <div style={footerBarsWrap}>
              <div style={footerBlue}></div>
              <div style={footerDark}></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
